import "./holdings.css";
import { INSTITUTIONS } from '../../../utils/parse13f';
import { buildSectorDistribution, getConcentrationBadge, getTopHoldingsConcentration } from '../../../utils/portfolioMetrics';

function formatChangeSummary(summary, lang) {
  if (!summary) return null;
  const labels = lang === 'zh'
    ? { new: '新增', increased: '增持', decreased: '减持', sold: '清仓' }
    : { new: 'New', increased: 'Added', decreased: 'Trimmed', sold: 'Sold' };
  return `${summary.new} ${labels.new} · ${summary.increased} ${labels.increased} · ${summary.decreased} ${labels.decreased} · ${summary.sold} ${labels.sold}`;
}

const Holdings13FPanel = ({
  activeInst,
  setActiveInst,
  holdings13f,
  holdings13fLoading,
  holdings13fError,
  navigate,
  lang,
  t,
}) => {
  const inst = INSTITUTIONS[activeInst];
  const displayName = lang === 'zh' ? inst.name : inst.nameEn;
  const description = lang === 'zh' ? inst.desc : inst.descEn;
  const concentration = getTopHoldingsConcentration(holdings13f?.holdings);
  const concentrationBadge = getConcentrationBadge(concentration, lang);
  const sectors = buildSectorDistribution(holdings13f?.holdings, lang).slice(0, 3);
  const changeSummary = formatChangeSummary(holdings13f?.changeSummary, lang);
  const qoqChange = holdings13f?.trend?.qoqChangePct;

  return (
    <div className="hp-pro-card hp-holdings-panel">
      <div className="hp-pro-card-title">{t('home.holdings13fTitle')}</div>
      <div className="hp-holdings-why">
        <div className="hp-holdings-why-title">{t('home.holdings13fWhy')}</div>
        <div className="hp-holdings-why-desc">{t('home.holdings13fWhyDesc')}</div>
      </div>

      <div className="hp-holdings-tabs">
        {INSTITUTIONS.map((item, idx) => (
          <button
            key={item.cik}
            className={`hp-holdings-tab ${idx === activeInst ? 'active' : ''}`}
            onClick={() => setActiveInst(idx)}
          >
            {item.short}
          </button>
        ))}
      </div>

      {holdings13fLoading ? (
        <div className="hp-holdings-loading">{t('home.holdings13fLoading')}</div>
      ) : holdings13fError ? (
        <div className="hp-holdings-error">{t('home.holdings13fError')}</div>
      ) : holdings13f ? (
        <div className="hp-holdings-content">
          <div className="hp-holdings-meta">
            <span className="hp-holdings-inst-name">{displayName}</span>
            {description && <span className="hp-holdings-inst-desc">{description}</span>}
            {holdings13f.filingDate && (
              <span className="hp-holdings-date">{t('home.holdings13fDate')}：{holdings13f.filingDate}</span>
            )}
            {holdings13f.totalValue > 0 && (
              <span className="hp-holdings-total">
                {t('home.holdings13fTotal')}：${(holdings13f.totalValue / 1e6).toFixed(1)}B
              </span>
            )}
            <span className={`hp-holdings-concentration ${concentrationBadge.className}`}>
              {concentrationBadge.label} {concentration.toFixed(1)}%
            </span>
            {qoqChange != null && (
              <span className={`hp-holdings-qoq ${qoqChange >= 0 ? 'positive' : 'negative'}`}>
                QoQ {qoqChange >= 0 ? '+' : ''}{qoqChange.toFixed(1)}%
              </span>
            )}
          </div>
          {changeSummary && <div className="hp-holdings-change-summary">{changeSummary}</div>}
          {sectors.length > 0 && (
            <div className="hp-holdings-sector-row">
              {sectors.map((sector) => (
                <span key={sector.key} className={`hp-holdings-sector sector-${sector.key}`}>
                  {sector.label} {sector.weight.toFixed(1)}%
                </span>
              ))}
            </div>
          )}

          <div className="hp-holdings-table-wrap">
            <table className="hp-holdings-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('home.holdings13fStock')}</th>
                  <th>{t('home.holdings13fShares')}</th>
                  <th>{t('home.holdings13fValue')}</th>
                  <th>{t('home.holdings13fWeight')}</th>
                </tr>
              </thead>
              <tbody>
                {holdings13f.holdings.slice(0, 10).map((h, i) => (
                  <tr key={h.cusip + i}>
                    <td className="hp-holdings-rank">{i + 1}</td>
                    <td className="hp-holdings-name">{h.nameOfIssuer}</td>
                    <td className="hp-holdings-shares">{h.shares.toLocaleString()}</td>
                    <td className="hp-holdings-value">${h.value.toLocaleString()}</td>
                    <td className="hp-holdings-weight">{h.weight}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="hp-holdings-source">{t('home.holdings13fSource')}</p>
          <div className="hp-holdings-view-detail" onClick={() => navigate(`/institution/${inst.cik}`)}>
            {t('home.holdings13fViewDetail')}
          </div>
        </div>
      ) : (
        <div className="hp-holdings-empty">{t('home.holdings13fNoData')}</div>
      )}
    </div>
  );
};

export default Holdings13FPanel;
